import React, { useEffect, useState, memo, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Pie } from "react-chartjs-2";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
Chart.register(CategoryScale);

const priceFormat = (price) => {
  return price.toLocaleString("en-US", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  });
};

export const Categorysales = memo(({}) => {
  const { t } = useTranslation();
  var currentTime = new Date();
  var currentyear = currentTime.getFullYear();
  var currentMonth = currentTime.getUTCMonth() + 1;
  const [filter, setFilter] = useState(0);
  const [totalamt, setTotalamt] = useState(0);
  const [totalcnt, setTotalcnt] = useState(0);
  const [typeFilter, setTypeFilter] = useState("qty");
  const [filtertext, setFilterText] = useState("");
  const [pizzaCount, setPizzaCount] = useState(0);
  const [pastaCount, setPastaCount] = useState(0);
  const [dessertsCount, setDessertsCount] = useState(0);
  const [pizzzSales, setPizzzSales] = useState(0);
  const [pastaSales, setPastaSales] = useState(0);
  const [dessertsSales, setDessertsSales] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const getCurrentFiscalYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    // Fiscal year starts in September
    const startYear = month >= 9 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
  };

  const [period, setPeriod] = useState(getCurrentFiscalYear());
  const [months, setMonths] = useState("9");
  const [quarter, setQuarter] = useState(0);
  const MonthNames = useMemo(
    () => [
      t("months.sep"),
      t("months.oct"),
      t("months.nov"),
      t("months.dec"),
      t("months.jan"),
      t("months.feb"),
      t("months.mar"),
      t("months.apr"),
      t("months.may"),
      t("months.jun"),
      t("months.jul"),
      t("months.aug"),
    ],
    [t],
  );
  const quarterNames = useMemo(
    () => ({
      1: t("quarter_sep_nov"),
      2: t("quarter_dec_feb"),
      3: t("quarter_mar_may"),
      4: t("quarter_jun_aug"),
    }),
    [t],
  );

  const getCurrentFiscalQuarter = () => {
    const month = new Date().getMonth() + 1; // 1–12
    if (month >= 9 && month <= 11) return 1; // Sep–Nov
    if (month === 12 || month <= 2) return 2; // Dec–Feb
    if (month >= 3 && month <= 5) return 3; // Mar–May
    return 4; // Jun–Aug
  };

  const getAvailableQuarters = (selectedFiscalYear) => {
    const currentFiscalYear = getCurrentFiscalYear();
    const currentQuarter = getCurrentFiscalQuarter();
    return Object.entries(quarterNames)
      .filter(([quarterNumber]) => {
        if (selectedFiscalYear === currentFiscalYear) {
          return Number(quarterNumber) <= currentQuarter;
        }
        return true; // show all for past years
      })
      .map(([quarterNumber, label]) => (
        <option key={quarterNumber} value={quarterNumber}>
          {label}
        </option>
      ));
  };

  useEffect(() => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const fiscalQuarterMap = {
      9: 1,
      10: 1,
      11: 1,
      12: 2,
      1: 2,
      2: 2,
      3: 3,
      4: 3,
      5: 3,
      6: 4,
      7: 4,
      8: 4,
    };
    const currentFiscalQuarter = fiscalQuarterMap[month];
    setQuarter(currentFiscalQuarter);
  }, []);

  const noDataPlugin = useMemo(
    () => ({
      id: "noDataPlugin",
      beforeDraw(chart) {
        const { datasets } = chart.data;
        const hasData = datasets.some((dataset) =>
          dataset.data.some((value) => value > 0),
        );
        if (!hasData) {
          const { ctx, width, height } = chart;
          ctx.save();
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = "14px Arial";
          ctx.fillStyle = "#ed5264"; // Color for the text
          ctx.fillText(t("no_data_filtered_search_txt"), width / 2, height / 2);
          ctx.restore();
        }
      },
    }),
    [t],
  );

  const computedChartData = useMemo(() => {
    const pieData =
      typeFilter === "qty"
        ? [pizzaCount, pastaCount, dessertsCount]
        : [pizzzSales, pastaSales, dessertsSales];
    return {
      labels: [
        t("categories.pizza"),
        t("categories.pasta_rice_noodle_sauces"),
        t("categories.desserts"),
      ],
      datasets: [
        {
          label:
            typeFilter === "qty"
              ? t("qty_sold_label")
              : t("sale_percentage_label"),
          data: pieData,
          type: "pie",
          backgroundColor: [
            "#a1e8eb",
            "rgba(195,183,248,1)",
            "rgba(164,218,184,1)",
            "rgba(141, 197, 228,1)",
          ],
          borderColor: "#e6e6e6",
          borderWidth: 1,
        },
      ],
    };
  }, [typeFilter,pizzaCount,pastaCount,dessertsCount,pizzzSales,pastaSales,dessertsSales,t]);

  useEffect(() => {
    setIsLoading(true);
    if (filter === "custom" && (!fromDate || !toDate)) {
      setIsLoading(false);
      // Don't make the API call if dates are missing for the "custom" filter
      return;
    }
    let payload = {};
    if (filter === "custom") {
      payload = {
        filter: filter,
        datefrom: format(new Date(fromDate), "yyyy-MM-dd"),
        dateto: format(new Date(toDate), "yyyy-MM-dd"),
      };
      setShowDatePicker(false);
    } else if (["weekly", "monthly", "quarterly", "FYdata"].includes(filter)) {
      payload = {
        filter: filter,
        datefrom: "",
        dateto: "",
        period: period,
      };
    } else {
      payload = {
        filter: parseInt(filter),
        datefrom: "",
        dateto: "",
      };
    }
    let URL = BaseUrl + "admin/categorysales";
    http
      .post(URL, payload)
      .then((res) => res.data.data)
      .then((response) => {
        setIsLoading(false);
        const actualMonth = parseInt(months);
        let filteredData = [];
        if (filter === "monthly") {
          filteredData = response.filter((item) => item.month === actualMonth);
        } else if (filter === "quarterly") {
          filteredData = response.filter(
            (item) => item.fiscalQuarter === Number(quarter),
          );
        } else {
          filteredData = response;
        }
        let pizzaarr = filteredData.filter((item) => item.categoryId === "1");
        let pastaarr = filteredData.filter(
              (item) => ["10", "13", "11", "12"].includes(item.categoryId)
        );
        let burritoarr = filteredData.filter(
          (item) => item.categoryId === "11" || item.categoryId === "12",
        );
        let dessertsarr = filteredData.filter(
          (item) => item.categoryId === "14",
        );
        let finalone = pizzaarr.concat(pastaarr, dessertsarr);
        let totalAmount = finalone.reduce((total, item) => {
          return total + item.amount;
        }, 0);
        let totalCount = finalone.reduce((total, item) => {
          return total + item.sum;
        }, 0);

        let pizzaCount = pizzaarr.reduce((total, item) => {
          return total + item.sum;
        }, 0);
        let pizzaAmount = pizzaarr.reduce((total, item) => {
          return total + item.amount;
        }, 0);
        let pastaCount = pastaarr.reduce((total, item) => {
          return total + item.sum;
        }, 0);
        let pastaAmount = pastaarr.reduce((total, item) => {
          return total + item.amount;
        }, 0);
        let dessertsCount = dessertsarr.reduce((total, item) => {
          return total + item.sum;
        }, 0);
        let dessertsAmount = dessertsarr.reduce((total, item) => {
          return total + item.amount;
        }, 0);
        let pizzaper = (pizzaAmount / totalAmount) * 100;
        let pastaper = (pastaAmount / totalAmount) * 100;
        let dessertper = (dessertsAmount / totalAmount) * 100;
        setPizzzSales(pizzaper.toFixed(2));
        setPastaSales(pastaper.toFixed(2));
        setDessertsSales(dessertper.toFixed(2));

        setPastaCount(pastaCount);
        setPizzaCount(pizzaCount);
        setDessertsCount(dessertsCount);
        setTotalamt(totalAmount);
        setTotalcnt(totalCount);
        // update completed — derived chart data will recompute via dependencies
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  }, [filter, fromDate, toDate, months, quarter, period]);

  const DateChange = (value, type) => {
    if (type === "from") {
      setFromDate(value);
      if (toDate && value && value > toDate) {
        setToDate(null);
      }
    } else if (type === "to") {
      if (!fromDate || value >= fromDate) {
        setToDate(value);
      } else {
        // Handle invalid date selection
        setToDate(null);
      }
    }
  };

  const changeFilterHandler = (e) => {
    const val = e.target.value;
    setFilter(val);
    setFilterText(e.target.options[e.target.selectedIndex].text);
    if (e.target.value === "custom") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      setFromDate(null);
      setToDate(null);
    }
  };

  const changeTypeFilterHandler = (e) => {
    setTypeFilter(e.target.value);
  };

  const legendMargin = {
    id: "legendMargin",
    beforeInit: function (chart) {
      const fitValue = chart.legend.fit;
      chart.legend.fit = function fit() {
        fitValue.bind(chart.legend)();
        return (this.height += -10);
      };
    },
  };

  const changePeriodHandler = (e) => {
    const newPeriod = e.target.value;
    setPeriod(newPeriod); // update selected fiscal year in state
    //split fiscal year
    const [newStartYear, newEndYear] = newPeriod.split("-").map(Number);
    const fiscalStartMonthIndex = 8; // 0-based index for September
    const today = new Date();
    const currentFiscalYear = getCurrentFiscalYear();
    //compare sleted year with currentfiscalyear
    const isCurrentFiscalYear = newPeriod === currentFiscalYear;
    // MONTHLY filter logic
    if (filter === "monthly") {
      let visibleMonthNames = MonthNames;
      if (isCurrentFiscalYear) {
        // Calculate current fiscal month index
        const currentMonthIndex = today.getMonth(); // 0-based
        const monthsPassedSinceFiscalStart =
          (currentMonthIndex - fiscalStartMonthIndex + 12) % 12;
        const maxVisibleIndex = monthsPassedSinceFiscalStart;
        visibleMonthNames = MonthNames.slice(0, maxVisibleIndex + 1);
      }
      const selectedMonthIndex = MonthNames.indexOf(months);
      const isValidMonth =
        selectedMonthIndex >= 0 &&
        selectedMonthIndex < visibleMonthNames.length;
      if (!isValidMonth) {
        const defaultMonthNumber = ((0 + fiscalStartMonthIndex) % 12) + 1;
        setMonths(String(defaultMonthNumber));
      }
    }
    // QUARTERLY filter logic
    if (filter === "quarterly") {
      const currentQuarter = getCurrentFiscalQuarter();
      let maxQuarter = 4;
      if (isCurrentFiscalYear) {
        maxQuarter = currentQuarter;
      }
      if (Number(quarter) > maxQuarter) {
        setQuarter(maxQuarter > 0 ? maxQuarter : 1);
      }
    }
  };

  const changeMonthHandler = (e) => {
    setMonths(e.target.value);
  };

  const formatDate = (dateString) => {
    const dateObject = new Date(dateString);
    const year = dateObject.getFullYear();
    const month = String(dateObject.getMonth() + 1).padStart(2, "0");
    const day = String(dateObject.getDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  };

  const options = [];
  for (let i = currentyear; i >= 2023; i--) {
    if (i === currentyear && currentMonth < 9) {
      continue;
    }
    const year = i;
    options.push(
      <option value={year + "-" + (year + 1)}>
        {year + "-" + (year + 1)}
      </option>,
    );
  }
  const currentFiscalYear = getCurrentFiscalYear();
  let visibleMonthNames = MonthNames;
  if (period === currentFiscalYear) {
    // Limit to current fiscal month
    const fiscalIndex = (new Date().getMonth() - 8 + 12) % 12; // Sep=0, Oct=1, ... Aug=11
    visibleMonthNames = MonthNames.slice(0, fiscalIndex + 1);
  }
  let loadingbuffer = isLoading ? "loading-buffer" : "";

  return (
    <div className={`chart-container chart-category-sales ${loadingbuffer}`}>
      <div className="chart-Hdr">
        <div className="chart-Hdr-txt">
          <h2 style={{ textAlign: "left" }}>{t("category_sales_txt")}</h2>
        </div>
        <div className="chart-fltr-hldr">
          <div
            className="chart-fltr"
            onMouseEnter={() => {
              if (filter === "custom" && !showDatePicker) {
                setShowDatePicker(true);
              }
            }}
            onMouseLeave={() => {
              if (filter === "custom" && showDatePicker) {
                setShowDatePicker(false);
              }
            }}
          >
            {
              <select
                value={typeFilter}
                onChange={(e) => changeTypeFilterHandler(e)}
              >
                <option value="qty">{t("qty_sold_option_txt")}</option>
                <option value="percentage">{t("sale_percentage_label")}</option>
              </select>
            }
            {
              <select value={filter} onChange={(e) => changeFilterHandler(e)}>
                <option value={0}>{t("all_txt")}</option>
                <option value={1}>{t("past_1_day_txt")}</option>
                <option value={"weekly"}>{t("current_week_txt")}</option>
                <option value={"monthly"}>{t("monthly_txt")}</option>
                <option value={"quarterly"}>{t("quarterly_txt")}</option>
                <option value={"FYdata"}>{t("fy_data_txt")}</option>
                <option value={30}>{t("past_30_days_txt")}</option>
                <option value={90}>{t("past_90_days_txt")}</option>
                <option value={180}>{t("past_180_days_txt")}</option>
                <option value={365}>{t("past_1_year_txt")}</option>
                <option value={"custom"}>{t("custom_date_txt")}</option>
              </select>
            }
            {showDatePicker && (
              <div className="date-picker-container">
                <label>{t("from_txt")}:</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(date) => DateChange(date, "from")}
                  showDisabledMonthNavigation
                  dateFormat="dd/MM/yyyy"
                  disablePast={true}
                  maxDate={new Date()}
                  placeholderText={t("select_date_txt")}
                />
                <label>{t("to_txt")}:</label>
                <DatePicker
                  selected={toDate}
                  showDisabledMonthNavigation
                  dateFormat="dd/MM/yyyy"
                  onChange={(date) => DateChange(date, "to")}
                  disablePast={true}
                  maxDate={new Date()}
                  placeholderText={t("select_date_txt")}
                />
              </div>
            )}
            {filter === "quarterly" && (
              <select
                value={quarter}
                onChange={(e) => setQuarter(Number(e.target.value))}
              >
                {getAvailableQuarters(period)}
              </select>
            )}
            {filter === "monthly" && (
              <select value={months} onChange={(e) => changeMonthHandler(e)}>
                {visibleMonthNames.map((monthName, index) => {
                  const monthNumber = ((index + 8) % 12) + 1; // fiscal-to-calendar month number
                  return (
                    <option key={monthNumber} value={monthNumber}>
                      {monthName}
                    </option>
                  );
                })}
              </select>
            )}
            {(filter === "monthly" ||
              filter === "quarterly" ||
              filter === "FYdata") && (
              <select value={period} onChange={(e) => changePeriodHandler(e)}>
                {options}
              </select>
            )}
          </div>
        </div>
        <div className="fltr-sub-val">
          <span className="fltr-val">
            {typeFilter === "qty"
              ? t("total_qty_sold_txt") + totalcnt
              : t("total_amount_ex_vat_txt") + priceFormat(totalamt)}
          </span>
          <span className="fltr-val">
            {[
              t("current_week_txt"),
              t("monthly_txt"),
              t("quarterly_txt"),
              t("fy_data_txt"),
            ].includes(filtertext)
              ? ""
              : filtertext != "" && filtertext != t("custom_date_txt")
                ? " (" + filtertext + ")"
                : filtertext === t("custom_date_txt") && fromDate && toDate
                  ? " (" +
                    formatDate(fromDate) +
                    " - " +
                    formatDate(toDate) +
                    ")"
                  : " (" + t("all_txt") + ")"}
          </span>
        </div>
      </div>
      {computedChartData && (
        <Pie
          data={computedChartData}
          plugins={[legendMargin, noDataPlugin]}
          options={{
            layout: {
              padding: {
                top: 30,
                right: 10,
                bottom: 20,
                left: 10,
              },
            },
            plugins: {
              title: {
                display: false,
              },
              legend: {
                display: true,
                align: "left",
                position: "bottom",
                labels: {
                  color: "#969696",
                  font: {
                    size: 11,
                    weight: 600,
                  },
                  boxWidth: 9,
                  padding: 20,
                },
              },
              tooltip: {
                titleColor: "rgba(220, 222, 226, 1)",
                bodyColor: "rgba(220, 222, 226, 1)",
              },
              datalabels: {
                display: false,
                font: {
                  weight: "bold",
                },
                anchor: "center",

                formatter: function (value) {
                  return value + (typeFilter === "percentage" ? "%" : "");
                },
              },
            },
          }}
        />
      )}
    </div>
  );
});
